import { signIn, signOut, useSession } from 'next-auth/react';
import router from 'next/router';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  activateEmployee,
  createEmployee,
  createOwner,
  deactivateEmployee,
  deleteEmployee,
  deleteOwner,
  fetchWorkshopDetailsByProfile,
  searchEmployees,
} from '../../../services/workshop';
import {
  getEmployeesList,
  getWorkshop,
  getWorkshopContact,
  getWorkshopOpeningHours,
  getWorkshopTypes,
  resetWorkshop,
} from '../../../store/actions';
import filterWorkshopTypes from '../../../utils/filterWorkshopTypes';
import WorkshopScreen from '../../scenes/workshop/WorkshopScreen';

const WorkshopScreenContainer = () => {
  const { data: session, status } = useSession();
  const workshop = useSelector((state) => state.workshop);
  const workshopContact = useSelector((state) => state.workshopContact);
  const workshopOpeningHours = useSelector((state) => state.workshopOpeningHours);
  const employees = useSelector((state) => state.employeesList.employees);
  const workshopTypes = useSelector((state) =>
    Object.keys(filterWorkshopTypes(state.workshopTypes)),
  );
  const [employeesList, setEmployeesList] = useState(null);
  const [searchTimer, setSearchTimer] = useState(null);
  const [isSearchEmployeeModalVisible, setIsSearchEmployeeModalVisible] = useState(false);
  const [isSearchEmployeeModalLoading, setIsSearchEmployeeModalLoading] = useState(false);
  const [currentType, setCurrentType] = useState(null);
  const [selectedMenu, setSelectedMenu] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isEmployeeLoading, setIsEmployeeLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const minimalCharsToSearch = 3;
  const minimalTimeInterval = 2000;

  const loadWorkshop = async () => {
    try {
      await refreshWorkshop();
      setIsLoading(false);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    if (session?.error === 'RefreshAccessTokenError' || status === 'unauthenticated') {
      signIn(); // Force sign in to hopefully resolve error
    }
    if (session?.user && status === 'authenticated') {
      loadWorkshop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.accessToken, session?.error, status]);

  useEffect(() => setEmployeesList(null), [isSearchEmployeeModalVisible]);

  const saveWorkshop = (data) => {
    if (data !== undefined && data?.length === 0) {
      dispatch(resetWorkshop());
    } else if (data !== undefined && data?.length > 0) {
      const firstWorkshopData = data[0];
      dispatch(getWorkshop(firstWorkshopData));
      dispatch(getWorkshopContact(firstWorkshopData));
      dispatch(getWorkshopOpeningHours(firstWorkshopData));
      dispatch(getEmployeesList(firstWorkshopData));
      dispatch(getWorkshopTypes(firstWorkshopData.workshop_id, firstWorkshopData.typ));
    }
  };

  const refreshWorkshop = async () => {
    await fetchWorkshopDetailsByProfile(
      session.user?.profileId,
      setError,
      saveWorkshop,
      logoutHandler,
    );
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    await refreshWorkshop();
    setIsRefreshing(false);
  };

  const editActiveEmployeeHandler = async (id, value) => {
    setIsEmployeeLoading(true);
    if (value) {
      await deactivateEmployee(id, workshop.workshopId, setError, refreshWorkshop, logoutHandler);
    } else {
      await activateEmployee(id, workshop.workshopId, setError, refreshWorkshop, logoutHandler);
    }
    await refreshWorkshop();
    setIsEmployeeLoading(false);
  };

  const editOwnerEmployeeHandler = async (id, value) => {
    setIsEmployeeLoading(true);
    if (value) {
      if (employees.filter((item) => item.employeeOwner).length > 1) {
        await deleteOwner(id, workshop.workshopId, setError, logoutHandler);
      }
    } else {
      await createOwner(id, workshop.workshopId, setError, logoutHandler);
    }
    await refreshWorkshop();
    setIsEmployeeLoading(false);
  };

  const deleteEmployeeHandler = async (value) => {
    setIsEmployeeLoading(true);
    await deleteEmployee(value, workshop.workshopId, setError, logoutHandler);
    await refreshWorkshop();
    setIsEmployeeLoading(false);
  };

  const searchHandler = async (value) => {
    if (value.length < minimalCharsToSearch) {
      return setEmployeesList(null);
    }
    setIsSearchEmployeeModalLoading(true);
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    return setSearchTimer(
      setTimeout(async () => {
        await searchEmployees(value, setEmployeesList, setError, logoutHandler);
        setIsSearchEmployeeModalLoading(false);
      }, minimalTimeInterval),
    );
  };

  const addEmployeeHandler = async (value) => {
    setIsRefreshing(true);
    await createEmployee(
      value,
      workshop.workshopId,
      refreshWorkshop,
      setError,
      setIsSearchEmployeeModalVisible,
      logoutHandler,
    );
    setIsRefreshing(false);
  };

  const priceListNavigator = () => {
    router.push({
      pathname: 'workshop/pricelist',
      query: { workshopId: workshop?.workshopId },
    });
  };

  const logoutHandler = async () => {
    await signOut();
  };

  const searchEmployeesHolder = {
    visible: isSearchEmployeeModalVisible,
    setVisible: setIsSearchEmployeeModalVisible,
    onChange: searchHandler,
    searchIsLoading: isSearchEmployeeModalLoading,
    employeesList,
    action: addEmployeeHandler,
    isLoading: isRefreshing,
  };

  return (
    <WorkshopScreen
      workshop={workshop}
      workshopContact={workshopContact}
      workshopOpeningHours={workshopOpeningHours}
      employees={employees}
      searchEmployeesHolder={searchEmployeesHolder}
      editActiveEmployeeHandler={editActiveEmployeeHandler}
      editOwnerEmployeeHandler={editOwnerEmployeeHandler}
      deleteEmployeeHandler={deleteEmployeeHandler}
      selectedMenu={selectedMenu}
      setSelectedMenu={setSelectedMenu}
      priceListNavigator={priceListNavigator}
      isLoading={isLoading}
      isEmployeeLoading={isEmployeeLoading}
      workshopTypes={workshopTypes}
      currentType={currentType}
      setCurrentType={setCurrentType}
      status={status}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      error={error}
    />
  );
};

export default WorkshopScreenContainer;
